import type { ExpressionSpecification, LayerSpecification } from 'maplibre-gl';
import type { StylePart } from '@/features/map';

// OSM footpaths and tracks from OpenFreeMap's OpenMapTiles vector tiles: free, no key, worldwide.
// Paths and tracks are only in tiles from z12; the source tops out at z14 and is overzoomed beyond.

const TRAILS_SOURCE = 'trails';
const MIN_ZOOM = 12;

export const TRAIL_LAYERS = ['trails-casing', 'trails-track', 'trails-path', 'trails-labels'];

const path: ExpressionSpecification = [
  'all',
  ['==', ['get', 'class'], 'path'],
  ['in', ['get', 'subclass'], ['literal', ['path', 'footway', 'steps', 'bridleway']]],
];
const track: ExpressionSpecification = ['==', ['get', 'class'], 'track'];

const width = (z12: number, z14: number, z17: number): ExpressionSpecification => [
  'interpolate',
  ['linear'],
  ['zoom'],
  12,
  z12,
  14,
  z14,
  17,
  z17,
];

/** Hiking paths (red) and tracks (brown), dashed over a light casing that keeps them readable on imagery. */
export const trailsStyle = (visible: boolean): StylePart => {
  const visibility = visible ? 'visible' : 'none';
  const line = {
    type: 'line',
    source: TRAILS_SOURCE,
    'source-layer': 'transportation',
    minzoom: MIN_ZOOM,
    layout: { visibility, 'line-join': 'round', 'line-cap': 'round' },
  } as const;
  const layers: LayerSpecification[] = [
    {
      ...line,
      id: 'trails-casing',
      filter: ['any', path, track],
      paint: {
        'line-color': 'rgba(255,255,255,.7)',
        'line-width': width(2, 3.5, 6),
        'line-opacity': ['interpolate', ['linear'], ['zoom'], 12, 0, 12.5, 1],
      },
    },
    {
      ...line,
      id: 'trails-track',
      filter: track,
      layout: { visibility, 'line-join': 'round' },
      paint: { 'line-color': '#8a5a2b', 'line-width': width(1, 1.6, 3), 'line-dasharray': [3, 1.5] },
    },
    {
      ...line,
      id: 'trails-path',
      filter: path,
      layout: { visibility, 'line-join': 'round' },
      paint: { 'line-color': '#d6271c', 'line-width': width(0.8, 1.5, 2.8), 'line-dasharray': [2, 1.2] },
    },
    {
      id: 'trails-labels',
      type: 'symbol',
      source: TRAILS_SOURCE,
      'source-layer': 'transportation_name',
      minzoom: 14,
      filter: ['any', path, track],
      layout: {
        visibility,
        'symbol-placement': 'line',
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Bold'],
        'text-size': 11,
      },
      paint: { 'text-color': '#7a1610', 'text-halo-color': 'rgba(255,255,255,.85)', 'text-halo-width': 1.2 },
    },
  ];
  return {
    // TileJSON rather than a tile URL: the tile URL embeds a build date that changes with every release.
    sources: { [TRAILS_SOURCE]: { type: 'vector', url: 'https://tiles.openfreemap.org/planet' } },
    layers,
  };
};
